import { MoreOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Dropdown,
  Form,
  Input,
  Modal,
  Table,
  Typography,
  message,
  notification,
} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React from 'react';
import { useCreateInvitation, useDeleteInvitation } from '../../api/Invitations';
import {
  useDeleteGuest,
  useSurveyClients,
  useSurveyInvitations,
  useUpdateGuest,
} from '../../api/Surveys';

const { Text } = Typography;

function InviteGuestModal({ visible, closeModal, surveyId }) {
  const [form] = Form.useForm();

  const { data: clients, isLoading: clientsLoading } = useSurveyClients(
    { surveyId },
    { enabled: visible },
  );
  const { data: invitations, isLoading: invitationsLoading } = useSurveyInvitations(
    { surveyId },
    { enabled: visible },
  );
  const { mutate: createInvitation, isLoading: createInvitationLoading } = useCreateInvitation();
  const { mutate: deleteInvitation, isLoading: deleteInvitationLoading } = useDeleteInvitation();
  const { mutate: updateGuest, isLoading: updateGuestLoading } = useUpdateGuest();
  const { mutate: deleteGuest, isLoading: deleteGuestLoading } = useDeleteGuest();

  const handleUpdateGuest = (userId, expirationStr) => {
    updateGuest(
      { userId, surveyId, data: { expires_at: expirationStr } },
      { onSuccess: () => message.success('Guest successfully updated') },
    );
  };

  const successDescription = (email) => (
    <>
      Invitation was successfully sent to:
      <br />
      <Text type="secondary">{email}</Text>
    </>
  );

  const onFinish = (formValues) => {
    const data = {
      ...formValues,
      survey_id: surveyId,
    };
    createInvitation(
      { surveyId, data },
      {
        onSuccess: () => {
          closeModal();
          notification.success({
            message: 'Invitation sent',
            description: successDescription(formValues.email),
            placement: 'top',
          });
        },
      },
    );
  };

  const columns = [
    {
      title: 'Current guests',
      dataIndex: 'email',
      render: (email, record) => {
        if (record.last_active) {
          return <Text>{email}</Text>;
        }
        return <Text type="secondary">{`${email} (pending)`}</Text>;
      },
    },
    {
      title: 'Last active',
      dataIndex: 'last_active',
      render: (date) => (date ? dayjs(date).format('MMM D, YYYY') : '-'),
    },
    {
      title: 'Expires at',
      dataIndex: 'expires_at',
      render: (_, record) => {
        if (record.expires_at) {
          return (
            <DatePicker
              defaultValue={dayjs(record.expires_at)}
              format="MMM D, YYYY"
              variant="borderless"
              allowClear={false}
              disabledDate={(current) => current.valueOf() < Date.now()}
              onChange={(newDate) => handleUpdateGuest(record.id, newDate.format('YYYY-MM-DD'))}
            />
          );
        }
        return '-';
      },
    },
    {
      key: 'action',
      width: 30,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: record.id,
                label: 'Remove',
              },
            ],
            onClick: () => {
              if (record.last_active) {
                deleteGuest(
                  { userId: record.id, surveyId },
                  { onSuccess: () => message.success('Guest successfully removed') },
                );
              } else {
                deleteInvitation(
                  { surveyId, invitationId: record.id },
                  { onSuccess: () => message.success('Guest successfully removed') },
                );
              }
            },
          }}
        >
          <Button type="text">
            <MoreOutlined />
          </Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <Modal
      title="Manage guests"
      open={visible}
      onOk={() => {
        form
          .validateFields()
          .then(onFinish)
          .catch(() => {});
      }}
      onCancel={closeModal}
      okText="Invite"
      confirmLoading={createInvitationLoading}
      destroyOnClose
      // default is 520
      width={775}
      maskClosable={false}
    >
      <Form form={form} autoComplete="off" preserve={false}>
        <Form.Item
          label="Email address"
          name="email"
          rules={[
            {
              required: true,
              type: 'email',
              message: 'Please enter a valid email address',
            },
          ]}
        >
          <Input placeholder="Enter email address" />
        </Form.Item>
      </Form>
      <Table
        columns={columns}
        dataSource={invitations && clients ? invitations.data.concat(clients.data) : []}
        size="small"
        pagination={{
          hideOnSinglePage: true,
        }}
        loading={
          clientsLoading ||
          invitationsLoading ||
          deleteGuestLoading ||
          deleteInvitationLoading ||
          updateGuestLoading
        }
        rowKey="id"
      />
    </Modal>
  );
}

InviteGuestModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  surveyId: PropTypes.number.isRequired,
};

export default InviteGuestModal;
